import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import type { Application, FederatedPointerEvent } from "pixi.js";
import type { ModsConfig } from "../mods";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

type InputButton = "left" | "right";

export interface ReplayFrame {
  t: number;
  x: number;
  y: number;
  buttons: InputButton[];
}

export interface ReplayBundle {
  metadata: any;
  seed: number;
  events: ReplayFrame[];
  mods: ModsConfig;
  deviceOffset: number;
  chartHash: string | null;
}

interface HitEvent {
  button: InputButton;
  x: number;
  y: number;
}

type HitHandler = (e: HitEvent) => void;

/**
 * Basic input manager that tracks cursor position and the state of two
 * primary buttons. Buttons can be triggered via keyboard (Z/X) or mouse
 * (left/right). "Hit" events are only emitted on the leading edge of a
 * press to prevent repeat hits from continuous presses. While a button is
 * held, the manager continuously polls cursor position each frame and emits
 * "hold" events so sliders can validate the follow radius for ticks and the
 * slider ball.
 */
export class InputManager {
  private cursorX = 0;
  private cursorY = 0;
  private pressed = new Set<InputButton>();
  private hitListeners = new Set<HitHandler>();
  private holdListeners = new Set<HitHandler>();
  private useDom: boolean;

  constructor(private app: Application, useDom = true) {
    this.useDom = useDom;
    if (useDom) {
      // Track cursor position.
      app.stage.eventMode = "static";
      app.stage.on("pointermove", this.onMove, this);
      app.stage.on("pointerdown", this.onPointerDown, this);
      app.stage.on("pointerup", this.onPointerUp, this);

      // Keyboard bindings.
      window.addEventListener("keydown", this.onKeyDown);
      window.addEventListener("keyup", this.onKeyUp);
    }

    // Poll button states each frame for sliders.
    app.ticker.add(this.poll);
  }

  destroy(): void {
    if (this.useDom) {
      this.app.stage.off("pointermove", this.onMove, this);
      this.app.stage.off("pointerdown", this.onPointerDown, this);
      this.app.stage.off("pointerup", this.onPointerUp, this);
      window.removeEventListener("keydown", this.onKeyDown);
      window.removeEventListener("keyup", this.onKeyUp);
    }
    this.app.ticker.remove(this.poll);
    this.hitListeners.clear();
    this.holdListeners.clear();
  }

  /** Current cursor X position. */
  get x(): number {
    return this.cursorX;
  }

  /** Current cursor Y position. */
  get y(): number {
    return this.cursorY;
  }

  /** Current set of pressed buttons. */
  get buttons(): InputButton[] {
    return Array.from(this.pressed);
  }

  onHit(cb: HitHandler): () => void {
    this.hitListeners.add(cb);
    return () => this.hitListeners.delete(cb);
  }

  onHold(cb: HitHandler): () => void {
    this.holdListeners.add(cb);
    return () => this.holdListeners.delete(cb);
  }

  private emitHit(button: InputButton): void {
    const ev = { button, x: this.cursorX, y: this.cursorY };
    for (const cb of this.hitListeners) cb(ev);
  }

  private emitHold(button: InputButton): void {
    const ev = { button, x: this.cursorX, y: this.cursorY };
    for (const cb of this.holdListeners) cb(ev);
  }

  private poll = (): void => {
    for (const btn of this.pressed) {
      this.emitHold(btn);
    }
  };

  private press(button: InputButton): void {
    if (!this.pressed.has(button)) {
      this.pressed.add(button);
      this.emitHit(button);
    }
  }

  private release(button: InputButton): void {
    this.pressed.delete(button);
  }

  /**
   * Update cursor position and button state directly. Used for deterministic
   * replays where input events are fed back into the manager.
   */
  feed(x: number, y: number, buttons: InputButton[]): void {
    this.cursorX = x;
    this.cursorY = y;
    for (const btn of Array.from(this.pressed)) {
      if (!buttons.includes(btn)) this.release(btn);
    }
    for (const btn of buttons) this.press(btn);
    // Immediately emit hold events for all currently pressed buttons
    // so replay frames can register slider ticks without waiting for
    // the next ticker poll.
    for (const btn of this.pressed) this.emitHold(btn);
  }

  private onMove = (e: FederatedPointerEvent): void => {
    this.cursorX = e.globalX;
    this.cursorY = e.globalY;
  };

  private onPointerDown = (e: FederatedPointerEvent): void => {
    this.cursorX = e.globalX;
    this.cursorY = e.globalY;
    const button: InputButton = e.button === 2 ? "right" : "left";
    this.press(button);
  };

  private onPointerUp = (e: FederatedPointerEvent): void => {
    const button: InputButton = e.button === 2 ? "right" : "left";
    this.release(button);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    let button: InputButton | null = null;
    if (e.code === "KeyZ") button = "left";
    else if (e.code === "KeyX") button = "right";
    if (!button) return;
    this.press(button);
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    let button: InputButton | null = null;
    if (e.code === "KeyZ") button = "left";
    else if (e.code === "KeyX") button = "right";
    if (!button) return;
    this.release(button);
  };
}

